(() => {
  if (customElements.get('stock-quantity')) return;

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const stockData = (element) => JSON.parse(element.querySelector('[data-variant-stock]').textContent);
  const root = () => window.Shopify?.routes?.root || '/';
  const request = async (url, options = {}) => {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), ...options });
    if (!response.ok) throw new Error('request_failed');
    return response;
  };
  // A product selector adds to the variant total; a cart selector replaces just its own line.
  const limits = (stock, ownQuantity = 0) => {
    const otherQuantity = Math.max(0, stock.cartQuantity - ownQuantity);
    const step = stock.step || 1;
    const min = Math.max(step, stock.min - otherQuantity);
    let max = Math.min(stock.inventory ?? Infinity, stock.max ?? Infinity) - otherQuantity;
    if (!stock.available) max = 0;
    max = max < min ? 0 : min + Math.floor((max - min) / step) * step;
    return { min, max, step };
  };

  customElements.define('stock-quantity', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[data-quantity-decrease]');
      this.plus = this.querySelector('[data-quantity-increase]');
      this.message = document.getElementById(this.input.getAttribute('aria-describedby'));
      this.savedQuantity = Number(this.input.value);
      this.min = Number(this.input.min) || 1;
      this.max = this.input.max === '' ? Infinity : Number(this.input.max);
      this.step = Number(this.input.step) || 1;
      this.minus.addEventListener('click', () => this.change(-this.step));
      this.plus.addEventListener('click', () => this.change(this.step));
      this.input.addEventListener('input', () => {
        this.updateState();
        this.dispatchEvent(new CustomEvent('quantity:input', { bubbles: true }));
      });
      this.input.addEventListener('change', () => {
        this.normalize();
        this.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true }));
      });
      if (this.querySelector('[data-variant-stock]')) this.setLimits(limits(stockData(this), this.savedQuantity), false);
      else this.updateState();
    }
    get valid() {
      const value = Number(this.input.value);
      return this.input.value !== '' && Number.isSafeInteger(value) && value >= this.min && value <= this.max && (value - this.min) % this.step === 0;
    }
    setLimits(next, normalize = true) {
      Object.assign(this, next);
      this.input.min = this.min;
      this.input.step = this.step;
      if (Number.isFinite(this.max)) this.input.max = this.max;
      else this.input.removeAttribute('max');
      if (normalize) this.normalize();
      else this.updateState();
    }
    normalize() {
      const original = this.input.value;
      const value = Number(original);
      if (this.max >= this.min) {
        const bounded = Math.max(this.min, Math.min(this.max, Number.isFinite(value) ? value : this.min));
        this.input.value = this.min + Math.floor((bounded - this.min) / this.step) * this.step;
      }
      this.updateState();
      return original === this.input.value && this.valid;
    }
    change(amount) {
      if (this.busy || this.max < this.min) return;
      this.input.value = (Number(this.input.value) || this.min) + amount;
      this.normalize();
      this.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true }));
    }
    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.updateState();
    }
    updateState() {
      const value = Number(this.input.value);
      this.minus.disabled = this.busy || value <= this.min || this.max < this.min;
      this.plus.disabled = this.busy || value >= this.max || this.max < this.min;
      this.input.disabled = this.busy || this.max < this.min;
      this.input.setAttribute('aria-invalid', String(!this.valid));
      if (this.message) this.message.textContent = this.max < this.min ? this.dataset.emptyText :
        value >= this.max ? this.dataset.limitText.replace('[quantity]', this.max) :
        !this.valid ? this.dataset.invalidText : '';
    }
  });

  customElements.define('product-stock', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form.noValidate = true;
      this.quantity = this.querySelector('stock-quantity');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.button = this.querySelector('[data-add-button]');
      this.error = this.querySelector('[data-stock-error]');
      this.cartLink = this.querySelector('[data-stock-cart-link]');
      this.stock = stockData(this);
      this.quantity.setLimits(limits(this.stock));
      this.variantInput.addEventListener('change', () => this.refresh());
      this.addEventListener('quantity:input', () => this.updateState());
      this.addEventListener('quantity:change', () => this.updateState());
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.add(); });
      this.refreshListener = () => { if (!this.submitting && !document.hidden) this.refresh(); };
      document.addEventListener('cart:updated', this.refreshListener);
      document.addEventListener('visibilitychange', this.refreshListener);
      window.addEventListener('pageshow', this.refreshListener);
      this.refresh();
    }
    disconnectedCallback() {
      this.controller?.abort();
      document.removeEventListener('cart:updated', this.refreshListener);
      document.removeEventListener('visibilitychange', this.refreshListener);
      window.removeEventListener('pageshow', this.refreshListener);
    }
    async refresh() {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const variantId = this.variantInput.value;
      this.ready = false;
      this.updateState();
      if (!variantId) return false;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const url = new URL(this.dataset.productUrl, location.origin);
        // Retain preview context when testing an unpublished theme.
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', 'product-inventory');
        url.searchParams.set('_stock', Date.now());
        const response = await request(url, { signal: controller.signal });
        const stock = stockData(parseHTML(await response.text()));
        if (controller !== this.controller || variantId !== this.variantInput.value) return false;
        if (String(stock.id) !== variantId) throw new Error('variant_mismatch');
        this.stock = stock;
        this.ready = true;
        this.quantity.setLimits(limits(stock));
        this.error.textContent = '';
        return true;
      } catch {
        if (controller === this.controller) this.error.textContent = this.dataset.loadError;
        return false;
      } finally {
        clearTimeout(timeout);
        if (controller === this.controller) this.updateState();
      }
    }
    updateState() {
      const blocked = !this.ready || !this.quantity.valid || this.submitting;
      this.quantity.setBusy(!this.ready || this.submitting);
      this.button.disabled = blocked;
      const payment = this.querySelector('.shopify-payment-button');
      if (payment) payment.inert = blocked;
      const options = this.querySelector('.product-options');
      if (options) options.inert = Boolean(this.submitting);
      this.button.setAttribute('aria-busy', String(Boolean(this.submitting)));
    }
    async add() {
      if (this.submitting) return;
      const requested = Number(this.quantity.input.value);
      const formData = new FormData(this.form);
      this.submitting = true;
      this.cartLink.hidden = true;
      this.updateState();
      try {
        if (!await this.refresh()) return;
        if (!this.quantity.valid || Number(this.quantity.input.value) !== requested) {
          this.error.textContent = this.dataset.changedText;
          return;
        }
        formData.set('quantity', requested);
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST', body: formData, headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(20000)
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          // Shopify can partially add on a stock error. Refresh before offering another add.
          await this.refresh();
          this.error.textContent = (typeof result.description === 'string' ? result.description + ' ' : '') + this.dataset.addError;
          this.cartLink.hidden = false;
          document.querySelector('quick-add-dialog')?.refreshCart(document.querySelector('[data-cart-section]')?.dataset.cartSection);
          return;
        }
        location.assign(this.dataset.cartUrl);
      } catch {
        await this.refresh();
        this.error.textContent = this.dataset.addError;
        this.cartLink.hidden = false;
      } finally {
        this.submitting = false;
        this.updateState();
      }
    }
  });

  customElements.define('stock-cart', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('quantity:input', () => { clearTimeout(this.timer); this.updateState(); });
      this.addEventListener('quantity:change', () => {
        this.updateState();
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.save(false), 350);
      });
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('#cart')) return;
        event.preventDefault();
        this.save(event.submitter?.name === 'checkout');
      });
      this.updateState();
    }
    disconnectedCallback() { clearTimeout(this.timer); }
    get quantities() { return [...this.querySelectorAll('stock-quantity')]; }
    updateState() {
      const quantities = this.quantities;
      const dirty = quantities.some((quantity) => Number(quantity.input.value) !== quantity.savedQuantity);
      const invalid = quantities.some((quantity) => !quantity.valid);
      quantities.forEach((quantity) => quantity.setBusy(Boolean(this.saving || this.syncFailed)));
      const form = this.querySelector('#cart');
      if (form) form.noValidate = true;
      this.querySelectorAll('[name="update"], [name="checkout"]').forEach((button) => { button.disabled = this.saving || this.syncFailed || invalid; });
      const payment = this.querySelector('.additional-checkout-buttons');
      if (payment) payment.inert = Boolean(this.saving || this.syncFailed || dirty || invalid);
      this.querySelector('.cart-lines')?.toggleAttribute('inert', Boolean(this.saving));
      this.setAttribute('aria-busy', String(Boolean(this.saving)));
    }
    async render() {
      const url = new URL(location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('_stock', Date.now());
      const response = await request(url);
      const updated = parseHTML(await response.text()).querySelector('stock-cart');
      if (!updated) throw new Error('cart_render_failed');
      this.innerHTML = updated.innerHTML;
      window.Shopify?.PaymentButton?.init();
      document.querySelector('quick-add-dialog')?.refreshCart(document.querySelector('[data-cart-section]')?.dataset.cartSection);
    }
    async save(checkout) {
      clearTimeout(this.timer);
      if (this.saving || this.syncFailed) return;
      const quantities = this.quantities;
      quantities.forEach((quantity) => quantity.normalize());
      if (quantities.some((quantity) => !quantity.valid)) { this.updateState(); return; }
      const changes = quantities.filter((quantity) => Number(quantity.input.value) !== quantity.savedQuantity)
        .map((quantity) => ({ key: quantity.dataset.lineKey, quantity: Number(quantity.input.value), previous: quantity.savedQuantity }));
      // Apply reductions first, so two lines sharing a variant can exchange units at the stock limit.
      changes.sort((a, b) => (a.quantity - a.previous) - (b.quantity - b.previous));
      if (!changes.length && !checkout) return;
      this.saving = true;
      this.updateState();
      let message = '';
      let focusId = document.activeElement?.closest('stock-quantity')?.querySelector('input').id;
      try {
        for (const change of changes) {
          const response = await fetch(`${root()}cart/change.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: change.key, quantity: change.quantity }), signal: AbortSignal.timeout(20000)
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            message = typeof result.description === 'string' ? result.description : this.dataset.updateError;
            break;
          }
          // Line keys may change with discounts. The server is authoritative even on a 200 response.
          const line = result.items.find((item) => item.key === change.key);
          if (!line || line.quantity !== change.quantity) {
            message = this.dataset.changedText;
            break;
          }
        }
        await this.render();
        const valid = this.quantities.every((quantity) => quantity.valid);
        if (checkout && !message && valid) location.assign(`${root()}checkout`);
        if (!valid && !message) message = this.dataset.changedText;
      } catch {
        message = this.dataset.updateError;
        // A timed-out mutation may have succeeded: never automatically retry it.
        try { await this.render(); } catch { this.syncFailed = true; message = this.dataset.reloadText; }
      } finally {
        this.saving = false;
        this.updateState();
        const error = this.querySelector('[data-stock-error]');
        if (error) {
          error.textContent = message;
          if (message) error.focus({ preventScroll: true });
        }
        if (!message && focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      }
    }
  });
})();
